import {
  Mesh,
  MeshBasicMaterial,
  SphereGeometry,
  type Scene,
} from 'three';
import type { Vector3D } from '../types';
import { raycastVoxels } from '../world/raycast';
import type { WorldManager } from '../world/WorldManager';
import { ITEM_CATALOG, ItemId, type ItemDef } from '../items/catalog';
import type { EnemyManager } from '../entities/EnemyManager';
import { BlockType, isMineable } from '../types';

export interface CombatHit {
  kind: 'block' | 'entity' | 'miss';
  damage: number;
  x?: number;
  y?: number;
  z?: number;
  blockType?: BlockType;
  /** True when mace (or similar) should break the hit block. */
  blockBroken?: boolean;
  enemyId?: string;
  enemyName?: string;
  enemyKilled?: boolean;
}

export interface ProjectileHit {
  enemyId: string;
  enemyName: string;
  damage: number;
  killed: boolean;
}

interface Projectile {
  mesh: Mesh;
  position: Vector3D;
  velocity: Vector3D;
  damage: number;
  life: number;
  ownerItem: ItemId;
}

/**
 * Melee swings + plasma bolts. Entity hits resolve through EnemyManager;
 * mace swings can smash soft blocks.
 */
export class CombatSystem {
  private readonly projectiles: Projectile[] = [];
  private readonly ballGeo = new SphereGeometry(0.08, 8, 8);
  private readonly ballMat = new MeshBasicMaterial({ color: 0xc77dff });
  private readonly scene: Scene;
  private readonly world: WorldManager;
  private enemies: EnemyManager | null = null;
  /** When true, hits are reported but EnemyManager HP is not mutated (client multiplayer). */
  private reportOnly = false;

  constructor(scene: Scene, world: WorldManager) {
    this.scene = scene;
    this.world = world;
  }

  setEnemyManager(enemies: EnemyManager): void {
    this.enemies = enemies;
  }

  setReportOnly(reportOnly: boolean): void {
    this.reportOnly = reportOnly;
  }

  /**
   * Resolve combat damage for the held item.
   * Tools deal reduced melee damage (catalog × TOOL_DAMAGE_SCALE);
   * dedicated weapons use full catalog damage. Blaster still fires a bolt.
   */
  fire(
    itemId: ItemId,
    origin: Vector3D,
    direction: Readonly<Vector3D>,
  ): CombatHit {
    const def = ITEM_CATALOG[itemId];
    const dir = { x: direction.x, y: direction.y, z: direction.z };
    const damage = combatDamageFor(def);

    if (itemId === ItemId.BlasterPlasma) {
      this.spawnBolt(origin, dir, def);
      return { kind: 'miss', damage };
    }

    const reach = def.reach > 0 ? def.reach : 3.2;

    // Prefer living targets over blocks.
    const entityHit = this.enemies?.raycast(origin, dir, reach) ?? null;
    const blockHit = raycastVoxels(this.world, origin, dir, reach);

    if (entityHit && (!blockHit || entityHit.distance <= blockHit.distance)) {
      if (this.reportOnly) {
        return {
          kind: 'entity',
          damage,
          enemyId: entityHit.enemyId,
          enemyName: 'enemy',
          enemyKilled: false,
        };
      }
      const result = this.enemies!.applyDamage(entityHit.enemyId, damage, dir);
      if (!result) return { kind: 'miss', damage };
      return {
        kind: 'entity',
        damage,
        enemyId: entityHit.enemyId,
        enemyName: result.name,
        enemyKilled: result.killed,
      };
    }

    if (!blockHit) return { kind: 'miss', damage };

    const canSmash =
      itemId === ItemId.MaceIron &&
      isMineable(blockHit.blockType) &&
      (blockHit.blockType === BlockType.Dirt || blockHit.blockType === BlockType.Stone);

    return {
      kind: 'block',
      damage,
      x: blockHit.x,
      y: blockHit.y,
      z: blockHit.z,
      blockType: blockHit.blockType,
      blockBroken: canSmash,
    };
  }

  /** True if an enemy is the closest hittable target within this item's reach. */
  hasEnemyInReach(
    itemId: ItemId,
    origin: Vector3D,
    direction: Readonly<Vector3D>,
  ): boolean {
    const def = ITEM_CATALOG[itemId];
    const dir = { x: direction.x, y: direction.y, z: direction.z };
    const reach = def.reach > 0 ? def.reach : 3.2;
    const entityHit = this.enemies?.raycast(origin, dir, reach) ?? null;
    if (!entityHit) return false;
    const blockHit = raycastVoxels(this.world, origin, dir, reach);
    return !blockHit || entityHit.distance <= blockHit.distance;
  }

  /**
   * Advance projectiles; resolve enemy hits before voxel collision.
   * Returns entity hits that occurred this tick.
   */
  update(dt: number): ProjectileHit[] {
    const hits: ProjectileHit[] = [];

    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const p = this.projectiles[i]!;
      p.life -= dt;
      p.position.x += p.velocity.x * dt;
      p.position.y += p.velocity.y * dt;
      p.position.z += p.velocity.z * dt;
      p.mesh.position.set(p.position.x, p.position.y, p.position.z);

      const enemyId = this.enemies?.hitTestPoint(p.position) ?? null;
      if (enemyId) {
        if (this.reportOnly) {
          hits.push({
            enemyId,
            enemyName: 'enemy',
            damage: p.damage,
            killed: false,
          });
        } else {
          const result = this.enemies!.applyDamage(enemyId, p.damage, p.velocity);
          if (result) {
            hits.push({
              enemyId,
              enemyName: result.name,
              damage: p.damage,
              killed: result.killed,
            });
          }
        }
        this.destroyProjectile(i);
        continue;
      }

      const voxel = this.world.worldToVoxel(p.position.x, p.position.y, p.position.z);
      if (
        this.world.isSolid(voxel.x, voxel.y, voxel.z) ||
        p.life <= 0 ||
        !this.world.inBounds(voxel.x, voxel.y, voxel.z)
      ) {
        this.destroyProjectile(i);
      }
    }

    return hits;
  }

  dispose(): void {
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      this.destroyProjectile(i);
    }
    this.ballGeo.dispose();
    this.ballMat.dispose();
  }

  private spawnBolt(origin: Vector3D, dir: Vector3D, def: ItemDef): void {
    const speed = 42;
    const mesh = new Mesh(this.ballGeo, this.ballMat);
    mesh.position.set(origin.x, origin.y, origin.z);
    this.scene.add(mesh);
    this.projectiles.push({
      mesh,
      position: { x: origin.x, y: origin.y, z: origin.z },
      velocity: { x: dir.x * speed, y: dir.y * speed, z: dir.z * speed },
      damage: def.damage,
      life: 1.2,
      ownerItem: ItemId.BlasterPlasma,
    });
  }

  private destroyProjectile(index: number): void {
    const p = this.projectiles[index];
    if (!p) return;
    p.mesh.removeFromParent();
    this.projectiles.splice(index, 1);
  }
}

/** Pickaxes / tools hit softer than dedicated weapons. */
const TOOL_DAMAGE_SCALE = 0.55;

function combatDamageFor(def: ItemDef): number {
  const base = def.damage;
  if (base <= 0) return 0;
  if (def.category === 'tool') {
    return Math.max(1, Math.round(base * TOOL_DAMAGE_SCALE));
  }
  return base;
}
